import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Toaster } from 'react-hot-toast';
import Routes from "./components/routes/routesContainer";
import { UserContext } from "./hooks/UserContext";
import { api } from "./services/baseUrl";

const App = () => {
  const { user, loading } = useContext(UserContext);
  const [siteSettings, setSiteSettings] = useState({ maintenance_mode: false, blocked_roles: [] });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const browserWindow = typeof window !== 'undefined' ? window : undefined;

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await api.get('/site-settings');
      setSiteSettings(response.data?.data || { maintenance_mode: false, blocked_roles: [] });
    } catch (error) {
      console.error('Erreur chargement paramètres site:', error);
      setSiteSettings({ maintenance_mode: false, blocked_roles: [] });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSiteSettings();

    const handleSettingsUpdate = () => {
      fetchSiteSettings();
    };

    browserWindow?.addEventListener('siteSettingsUpdated', handleSettingsUpdate);
    return () => browserWindow?.removeEventListener('siteSettingsUpdated', handleSettingsUpdate);
  }, [browserWindow, fetchSiteSettings]);

  const currentPath = browserWindow?.location?.pathname || '/';
  const currentRole = Number(user?.id_role ?? user?.role_id ?? 0);
  const isAdmin = currentRole === 3;

  const blockedRoles = useMemo(
    () => (Array.isArray(siteSettings?.blocked_roles) ? siteSettings.blocked_roles.map(Number) : []),
    [siteSettings]
  );

  const maintenanceEnabled = Boolean(siteSettings?.maintenance_mode);
  const roleBlocked = !isAdmin && currentRole > 0 && blockedRoles.includes(currentRole);
  const isAdminRoute = currentPath.startsWith('/admin');
  const canBypassMaintenance = (Boolean(user) && isAdmin) || isAdminRoute || currentPath === '/login';
  const showMaintenance = !canBypassMaintenance && maintenanceEnabled;

  const handleBlockedLogout = () => {
    localStorage.removeItem('access_token');
    if (browserWindow) {
      browserWindow.location.href = '/';
    }
  };

  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1114] text-white">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-rose-500 border-t-transparent" />
      </div>
    );
  }

  if (showMaintenance || roleBlocked) {
    const title = roleBlocked
      ? 'Accès temporairement suspendu'
      : (siteSettings?.maintenance_title || 'Site en maintenance');
    const description = roleBlocked
      ? 'Votre rôle est temporairement bloqué par l’administration. Merci de réessayer plus tard.'
      : (siteSettings?.maintenance_message || 'Le site est actuellement en maintenance. Merci de revenir dans quelques instants.');

    return (
      <div className="min-h-screen bg-[#0f1114] text-white flex items-center justify-center px-6">
        <div className="max-w-2xl rounded-[28px] border border-white/10 bg-[#0b0b0c] p-10 text-center shadow-[0_25px_80px_rgba(0,0,0,0.45)]">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-rose-500/15 text-3xl">
            {roleBlocked ? '⛔' : '🛠️'}
          </div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-rose-400">Krayne Agency</p>
          <h1 className="text-3xl font-bold">{title}</h1>
          <p className="mt-4 text-base text-white/70">{description}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {roleBlocked && user && (
              <button
                type="button"
                onClick={handleBlockedLogout}
                className="inline-flex rounded-full border border-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/5"
              >
                Déconnexion
              </button>
            )}
            {!user && currentPath !== '/login' && (
              <a
                href="/login"
                className="inline-flex rounded-full bg-rose-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-rose-600"
              >
                Accès administration
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a1b',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes />
    </>
  );
};

export default App;